import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { KestrelInspectionType, KVAnnotation } from 'types'

import { getSeverityConfiguration } from './severityConfigurations'

const DEBOUNCE_DELAY_MS = 500

/**
 * Unified hook for detecting annotation changes and triggering saves.
 * - Debounces text fields (label, notes) to avoid excessive saves while typing
 * - Saves immediately for discrete changes (severity, tags, damage types, photos)
 * - Flushes pending saves on unmount or when switching annotations
 *
 * Captures the save function at the time of change to ensure correct data
 * is saved even if the user switches annotations before debounce fires.
 */
export const useAnnotationChangeManager = (
  editedAnnotation: KVAnnotation,
  inspectionType: KestrelInspectionType,
  quickSave: () => void
) => {
  // Extract individual field values for stable dependency tracking
  const annotationId = editedAnnotation.id
  const annotationLabel = editedAnnotation.label
  const annotationNotes = editedAnnotation.notes

  const severityConfig = getSeverityConfiguration(inspectionType)
  const currentSeverity = severityConfig.getCurrentSeverity(editedAnnotation)

  // Serialize complex objects for stable comparison
  const tagsJson = JSON.stringify(editedAnnotation.tags)
  const navyJson = JSON.stringify(editedAnnotation.navyAnnotation)
  const g1n6Json = JSON.stringify(editedAnnotation.g1n6Annotation)
  const aerospaceJson = JSON.stringify(editedAnnotation.aerospaceAnnotation)
  const rooftopJson = JSON.stringify(editedAnnotation.rooftopAnnotation)
  const imageNamesJson = JSON.stringify(editedAnnotation.imageNames)

  // Refs to track previous values
  const prevValuesRef = useRef({
    id: annotationId,
    label: annotationLabel,
    notes: annotationNotes,
    severity: currentSeverity,
    tagsJson,
    navyJson,
    g1n6Json,
    rooftopJson,
    aerospaceJson,
    imageNamesJson,
  })
  const isInitializedRef = useRef(false)

  // Capture the save function at the time of change, not at flush time.
  // This ensures we save the correct annotation's data even if the user
  // switches annotations before the debounce fires.
  const pendingSaveRef = useRef<(() => void) | null>(null)

  const debouncedSave = useMemo(
    () =>
      debounce(() => {
        if (pendingSaveRef.current) {
          pendingSaveRef.current()
          pendingSaveRef.current = null
        }
      }, DEBOUNCE_DELAY_MS),
    []
  )

  // Queue a debounced save, capturing the current quickSave function
  const queueDebouncedSave = useCallback(() => {
    pendingSaveRef.current = quickSave
    debouncedSave()
  }, [quickSave, debouncedSave])

  // Execute immediate save and clear any pending debounced save
  const saveImmediately = useCallback(() => {
    debouncedSave.cancel()
    pendingSaveRef.current = null
    quickSave()
  }, [quickSave, debouncedSave])

  // Flush pending saves on unmount
  useEffect(() => {
    return () => {
      debouncedSave.flush()
    }
  }, [debouncedSave])

  // Handle annotation switching - flush pending saves for previous annotation
  useEffect(() => {
    const prevId = prevValuesRef.current.id

    if (prevId && prevId !== annotationId) {
      // Flush before resetting - this uses the captured save function
      debouncedSave.flush()
      isInitializedRef.current = false
      prevValuesRef.current = {
        id: annotationId,
        label: annotationLabel,
        notes: annotationNotes,
        severity: currentSeverity,
        tagsJson,
        navyJson,
        g1n6Json,
        rooftopJson,
        aerospaceJson,
        imageNamesJson,
      }
    }
  }, [
    annotationId,
    annotationLabel,
    annotationNotes,
    currentSeverity,
    tagsJson,
    navyJson,
    g1n6Json,
    rooftopJson,
    aerospaceJson,
    imageNamesJson,
    debouncedSave,
  ])

  // Main change detection effect - uses primitive/string deps only
  useEffect(() => {
    const hasValidLabel = annotationLabel && annotationLabel.trim() !== ''
    const prev = prevValuesRef.current

    // Initialize on first render
    if (!isInitializedRef.current) {
      prevValuesRef.current = {
        id: annotationId,
        label: annotationLabel,
        notes: annotationNotes,
        severity: currentSeverity,
        tagsJson,
        navyJson,
        g1n6Json,
        rooftopJson,
        aerospaceJson,
        imageNamesJson,
      }
      isInitializedRef.current = true
      return
    }

    // Detect what changed
    const labelChanged = annotationLabel !== prev.label
    const notesChanged = annotationNotes !== prev.notes
    const severityChanged = currentSeverity !== prev.severity
    const tagsChanged = tagsJson !== prev.tagsJson
    const navyChanged = navyJson !== prev.navyJson
    const g1n6Changed = g1n6Json !== prev.g1n6Json
    const rooftopChanged = rooftopJson !== prev.rooftopJson
    const aerospaceChanged = aerospaceJson !== prev.aerospaceJson
    const imageNamesChanged = imageNamesJson !== prev.imageNamesJson

    // Update previous values
    prevValuesRef.current = {
      id: annotationId,
      label: annotationLabel,
      notes: annotationNotes,
      severity: currentSeverity,
      tagsJson,
      navyJson,
      g1n6Json,
      rooftopJson,
      aerospaceJson,
      imageNamesJson,
    }

    // Don't save if no valid label
    if (!hasValidLabel) return

    // Text fields use debounced save (user is typing)
    // queueDebouncedSave captures the current quickSave for later execution
    if (labelChanged || notesChanged) {
      queueDebouncedSave()
      return
    }

    // Discrete changes save immediately
    if (
      severityChanged ||
      tagsChanged ||
      navyChanged ||
      g1n6Changed ||
      rooftopChanged ||
      imageNamesChanged ||
      aerospaceChanged
    ) {
      saveImmediately()
    }
  }, [
    annotationId,
    annotationLabel,
    annotationNotes,
    currentSeverity,
    tagsJson,
    navyJson,
    g1n6Json,
    rooftopJson,
    aerospaceJson,
    imageNamesJson,
    queueDebouncedSave,
    saveImmediately,
  ])

  return {
    flushPendingSave: () => debouncedSave.flush(),
  }
}
